import { useState, useEffect, useCallback } from "react";
import { Loading } from "components";
import { useAppModal } from "components/common/AppModalProvider";
import { get, post } from "utils";

const baseUrl = `${process.env.REACT_APP_API_URL}/admin`;

interface PaymentItem {
  id: string;
  status: string;
  paidAmount: number;
  paymentKey: string | null;
  orderId: string | null;
  tossPaymentStatus: string | null;
  guestName: string;
  guestEmail: string;
  hostName: string;
  serviceTitle: string;
  scheduleDate: string;
  scheduleStartTime: string;
  paidAt: string | null;
  cancelledAt: string | null;
  createdAt: string;
}

const statusColors: Record<string, string> = {
  PENDING: "bg-yellow-100 text-yellow-700",
  PAID: "bg-green-100 text-green-700",
  COMPLETED: "bg-blue-100 text-blue-700",
  CANCELLED: "bg-red-100 text-red-500",
};

const PaymentManageComponent = () => {
  const { showAlert, showConfirm } = useAppModal();
  const [payments, setPayments] = useState<PaymentItem[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const limit = 20;

  const fetchPayments = useCallback(async () => {
    setLoading(true);
    try {
      const res: any = await get(`${baseUrl}/payment-list?page=${page}&limit=${limit}`);
      setPayments(res.data.data.items);
      setTotal(res.data.data.total);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => {
    fetchPayments();
  }, [fetchPayments]);

  const handleCancelPayment = async (item: PaymentItem) => {
    const confirmed = await showConfirm(
      `${item.guestName}님의 "${item.serviceTitle}" 결제를 취소하시겠습니까?\n(${item.paidAmount.toLocaleString()}원 환불)`,
      { title: "결제 취소", confirmText: "취소하기", variant: "warning" }
    );
    if (!confirmed) return;

    try {
      await post(`${baseUrl}/cancel-payment`, {
        bookingId: item.id,
        cancelReason: "관리자에 의한 결제 취소",
      });
      showAlert("결제가 취소되었습니다.", { variant: "success" });
      fetchPayments();
    } catch (e: any) {
      showAlert(
        e?.response?.data?.message || "결제 취소에 실패했습니다.",
        { variant: "error" }
      );
    }
  };

  const totalPages = Math.ceil(total / limit);

  if (loading) return <Loading />;

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-bold text-achromatic-900">
          💳 결제 관리
          <span className="text-sm font-normal text-achromatic-400 ml-2">
            총 {total}건
          </span>
        </h2>
      </div>

      <div className="bg-white rounded-xl border border-achromatic-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-achromatic-50 text-achromatic-500 text-[12px]">
                <th className="text-left px-4 py-3 font-medium">서비스</th>
                <th className="text-left px-4 py-3 font-medium">게스트</th>
                <th className="text-left px-4 py-3 font-medium">호스트</th>
                <th className="text-left px-4 py-3 font-medium">금액</th>
                <th className="text-left px-4 py-3 font-medium">상태</th>
                <th className="text-left px-4 py-3 font-medium">결제수단</th>
                <th className="text-left px-4 py-3 font-medium">일시</th>
                <th className="text-left px-4 py-3 font-medium">관리</th>
              </tr>
            </thead>
            <tbody>
              {payments.map((item) => (
                <tr
                  key={item.id}
                  className="border-t border-achromatic-50 hover:bg-achromatic-50/50"
                >
                  <td className="px-4 py-3">
                    <div className="font-medium text-achromatic-900 line-clamp-1 max-w-[160px]">
                      {item.serviceTitle}
                    </div>
                    <div className="text-[11px] text-achromatic-400">
                      {item.scheduleDate} {item.scheduleStartTime?.slice(0, 5)}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-achromatic-700">
                    {item.guestName}
                  </td>
                  <td className="px-4 py-3 text-achromatic-700">
                    {item.hostName}
                  </td>
                  <td className="px-4 py-3 font-semibold text-achromatic-900">
                    {item.paidAmount.toLocaleString()}원
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={`text-[11px] font-semibold px-2 py-1 rounded-md ${
                        statusColors[item.status] || "bg-achromatic-100 text-achromatic-500"
                      }`}
                    >
                      {item.status}
                    </span>
                    {item.tossPaymentStatus && (
                      <div className="text-[10px] text-achromatic-400 mt-0.5">
                        {item.tossPaymentStatus}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-[11px] text-achromatic-400">
                    {item.paymentKey ? (
                      item.paymentKey.startsWith("TP_") ? (
                        <span className="text-purple-600 font-semibold">포트원 (갤럭시아)</span>
                      ) : (
                        <span className="text-blue-500 font-semibold">토스페이먼츠</span>
                      )
                    ) : (
                      <span className="text-achromatic-300">Klav</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-[11px] text-achromatic-400">
                    {item.paidAt
                      ? new Date(item.paidAt).toLocaleString("ko-KR", {
                          month: "2-digit",
                          day: "2-digit",
                          hour: "2-digit",
                          minute: "2-digit",
                        })
                      : "-"}
                    {item.cancelledAt && (
                      <div className="text-red-400">
                        취소:{" "}
                        {new Date(item.cancelledAt).toLocaleString("ko-KR", {
                          month: "2-digit",
                          day: "2-digit",
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {item.status === "PAID" && item.paymentKey && (
                      <button
                        onClick={() => handleCancelPayment(item)}
                        className="text-[11px] font-semibold text-red-500 bg-red-50 px-2.5 py-1 rounded-lg hover:bg-red-100 transition-colors"
                      >
                        결제 취소
                      </button>
                    )}
                  </td>
                </tr>
              ))}
              {payments.length === 0 && (
                <tr>
                  <td
                    colSpan={8}
                    className="text-center py-10 text-achromatic-400"
                  >
                    결제 내역이 없습니다.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center items-center gap-2 mt-4">
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
            className="px-3 py-1.5 rounded-lg text-xs font-medium bg-achromatic-100 text-achromatic-600 hover:bg-achromatic-200 disabled:opacity-50"
          >
            이전
          </button>
          <span className="text-xs text-achromatic-500">
            {page} / {totalPages}
          </span>
          <button
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
            className="px-3 py-1.5 rounded-lg text-xs font-medium bg-achromatic-100 text-achromatic-600 hover:bg-achromatic-200 disabled:opacity-50"
          >
            다음
          </button>
        </div>
      )}
    </div>
  );
};

export default PaymentManageComponent;
